import * as React                      from 'react'
import { ModelController, ModelState } from '../core/modelController'
import { ModelData }                   from '../core/modelDataParser'

type Data = ModelState

type Actions = {
  togglePause: () => void
  setAnimation: (seqIndex: number) => void
  showSubModel: (bodyPartIndex: number, subModelIndex: number) => void
  setPlaybackRate: (rate: number) => void
  setFrame: (frame: number) => void
  setTempPaused: (isTempPaused: boolean) => void
}

type Props = {
  modelData: ModelData
  modelController: ModelController
  onModelStateEdit?: (modelState: ModelState) => void
  children: (data: Data, actions: Actions) => React.ReactNode
}

/**
 * Controller container
 */
export const ControllerContainer = (props: Props) => {
  const [modelState, setModelState] = React.useState(() => props.modelController.getCurrentState())
  const previousPauseStateRef = React.useRef(false)

  React.useEffect(() => {
    if (props.modelController) {
      setModelState(props.modelController.getCurrentState())
    }
  }, [props.modelController])

  React.useEffect(() => {
    let frameId = 0

    const syncState = () => {
      setModelState(props.modelController.getCurrentState())
      frameId = requestAnimationFrame(syncState)
    }

    frameId = requestAnimationFrame(syncState)

    return () => cancelAnimationFrame(frameId)
  }, [props.modelController])

  const commitModelStateEdit = (nextModelState: ModelState) => {
    setModelState(nextModelState)
    props.onModelStateEdit?.(nextModelState)
  }

  return (
    <React.Fragment>
      {props.children(modelState, {
        togglePause:  () => commitModelStateEdit(props.modelController.setPause(!modelState.isPaused)),
        setAnimation: seqIndex => commitModelStateEdit(props.modelController.setAnimation(seqIndex)),
        showSubModel: (bodyPartIndex, subModelIndex) =>
          commitModelStateEdit(props.modelController.showSubModel(bodyPartIndex, subModelIndex)),
        setPlaybackRate: rate => commitModelStateEdit(props.modelController.setPlaybackRate(rate)),
        setFrame:        frame => commitModelStateEdit(props.modelController.setFrame(frame)),
        setTempPaused:   isTempPaused => {
          if (isTempPaused) {
            previousPauseStateRef.current = modelState.isPaused
            commitModelStateEdit(props.modelController.setPause(isTempPaused))
          } else {
            commitModelStateEdit(props.modelController.setPause(previousPauseStateRef.current))
          }
        }
      })}
    </React.Fragment>
  )
}
